import BackendService from 'BackendService';
import { useCallback, useEffect, useState } from 'react';
import { ActivityLogModelType } from '../../models/ActivityLog';
import { ActivityLogFromAPI } from './fullContentDiffViewerTypes';
import { hasVersionDiffPaths } from './fullContentDiffViewerUtils';

interface UseVersionHistoryOptions {
  isOpen: boolean;
  modelType: ActivityLogModelType;
  modelId: string;
  onVersionsLoaded?: (versions: ActivityLogFromAPI[]) => void;
  selectedVersionId?: string;
  onVersionSelect: (version: ActivityLogFromAPI) => void;
}

export function useVersionHistory({
  isOpen,
  modelType,
  modelId,
  onVersionsLoaded,
  selectedVersionId,
  onVersionSelect,
}: UseVersionHistoryOptions) {
  const [versions, setVersions] = useState<ActivityLogFromAPI[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchVersions = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const backendService = new BackendService();
      const response = await backendService.getActivityLogs(modelType, modelId, 1, 50, {
        eventType: 'CONTENT_DRAFT_SAVED',
      });

      if (Array.isArray(response.data)) {
        const versionsWithDiff = response.data.filter((version) =>
          hasVersionDiffPaths(version as unknown as ActivityLogFromAPI),
        );
        setVersions(versionsWithDiff as unknown as ActivityLogFromAPI[]);
        onVersionsLoaded?.(versionsWithDiff as unknown as ActivityLogFromAPI[]);
      } else {
        setError('Invalid response format - expected array');
      }
    } catch {
      setError('Failed to load version history');
    } finally {
      setLoading(false);
    }
  }, [modelType, modelId, onVersionsLoaded]);

  useEffect(() => {
    if (isOpen) {
      fetchVersions();
    }
  }, [isOpen, fetchVersions]);

  useEffect(() => {
    if (versions.length > 0 && !selectedVersionId) {
      const versionWithContent = versions.find(hasVersionDiffPaths);
      if (versionWithContent) {
        onVersionSelect(versionWithContent);
      }
    }
  }, [versions, selectedVersionId, onVersionSelect]);

  return { versions, loading, error };
}
